// react
import { Suspense, useCallback, useRef } from 'react';
import type { ComponentType } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import { Box, CircularProgress, Grid, Skeleton, useTheme } from '@mui/material';

// api — generated hooks
import { useGetSubscriptionSubscriptionAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// components
import PortfolioAnalysisServiceCard from '@/features/energyxplorer/portfolio/components/datagrid/PortfolioAnalysisServiceCard';
import DataIntegrityDialog from '@/features/energyxplorer/portfolio/components/meter-drawer/data-integrity/DataIntegrityDialog';
import usePortfolioAnalysisRows from '@/features/energyxplorer/portfolio/hooks/usePortfolioAnalysisRows';
import { meanByType } from '@/features/energyxplorer/portfolio/utils/portfolio-analysis-helpers';
import useSearchParams from '@/hooks/useStableSearchParams';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

interface PortfolioAnalysisDashboardProps {
  DatagridComponent: ComponentType<Record<string, unknown>>;
}

/**
 * Shared portfolio analysis dashboard. Renders the service overview cards
 * and a datagrid provided by the caller via `DatagridComponent`.
 */
const PortfolioAnalysisDashboard = ({
  DatagridComponent,
}: PortfolioAnalysisDashboardProps) => {
  const theme = useTheme();
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  const navigate = useNavigate();
  const pendingMeterTypeRef = useRef<'matrikel' | 'meter'>('matrikel');

  const setMeterType = useCallback((type: string) => {
    pendingMeterTypeRef.current = type as 'matrikel' | 'meter';
  }, []);

  const handleSetSelectedMeter = useCallback(
    (meter: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigate as any)({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          meterId: meter.matrikel_key as string,
          meterSubId: meter.UnitId
            ? String(meter.UnitId)
            : meter.MeteringPointId
              ? String(meter.MeteringPointId)
              : undefined,
          meterRowType: pendingMeterTypeRef.current,
          drawerAnalysisType: (meter.type as string) || 'power',
        }),
      });
    },
    [navigate],
  );
  const account = useSelector(getSelectedAccount);

  const { data: subscriptionData } =
    useGetSubscriptionSubscriptionAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const {
    matrikel_list,
    isFetching,
    powerSummedData,
    heatSummedData,
    waterSummedData,
    enrichedData,
  } = usePortfolioAnalysisRows();

  if (!enrichedData) return null;

  // Cast to any[] so service-card reduce/filter/map can access dynamic fields
  const rows$ = enrichedData.portfolio_analysis_list as Array<
    Record<string, unknown>
  >;
  const getTrend = (
    currentValue: number,
    lastYearValue: number,
  ): number | undefined => {
    if (lastYearValue <= 0) {
      return undefined;
    }

    return ((currentValue - lastYearValue) / lastYearValue) * 100;
  };

  return (
    <PageContainer>
      <Grid container spacing={2} maxHeight={'80vh'}>
        <Suspense fallback={<Skeleton />}>
          {subscriptionData &&
            !isFullscreen &&
            [
              {
                disabled: !subscriptionData?.power,
                label: t('cards.power.title'),
                path: '../../power/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.power_total_consumption_year != null
                      ? acc + (o.power_total_consumption_year as number)
                      : acc,
                  0,
                ),
                trend: getTrend(
                  rows$.reduce(
                    (acc, o) =>
                      o.power_total_consumption_year != null
                        ? acc + (o.power_total_consumption_year as number)
                        : acc,
                    0,
                  ),
                  rows$.reduce(
                    (acc, o) =>
                      o.power_total_consumption_last_year != null
                        ? acc + (o.power_total_consumption_last_year as number)
                        : acc,
                    0,
                  ),
                ),
                unit: t('cards.power.unit'),
                icon: <BoltIcon />,
                color: theme.palette.power.main,
                dataPct: meanByType(rows$, 'power', 'one_year_data_pct'),
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'power'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'power')
                    .map((item) => item.main_address),
                ).size,
                summedData: powerSummedData ?? [],
              },
              {
                disabled: !subscriptionData?.heat,
                label: t('cards.heat.title'),
                path: '../../heat/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.heat_total_consumption_year != null
                      ? acc + (o.heat_total_consumption_year as number)
                      : acc,
                  0,
                ),
                trend: getTrend(
                  rows$.reduce(
                    (acc, o) =>
                      o.heat_total_consumption_year != null
                        ? acc + (o.heat_total_consumption_year as number)
                        : acc,
                    0,
                  ),
                  rows$.reduce(
                    (acc, o) =>
                      o.heat_total_consumption_last_year != null
                        ? acc + (o.heat_total_consumption_last_year as number)
                        : acc,
                    0,
                  ),
                ),
                unit: t('cards.heat.unit'),
                icon: <LocalFireDepartmentRoundedIcon />,
                color: theme.palette.heat.main,
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'heat'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                dataPct: meanByType(rows$, 'heat', 'one_year_data_pct'),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'heat')
                    .map((item) => item.main_address),
                ).size,
                summedData: heatSummedData ?? [],
              },
              {
                disabled: !subscriptionData?.water,
                label: t('cards.water.title'),
                path: '../../water/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.water_total_consumption_year != null
                      ? acc + (o.water_total_consumption_year as number)
                      : acc,
                  0,
                ),
                trend: getTrend(
                  rows$.reduce(
                    (acc, o) =>
                      o.water_total_consumption_year != null
                        ? acc + (o.water_total_consumption_year as number)
                        : acc,
                    0,
                  ),
                  rows$.reduce(
                    (acc, o) =>
                      o.water_total_consumption_last_year != null
                        ? acc + (o.water_total_consumption_last_year as number)
                        : acc,
                    0,
                  ),
                ),
                unit: t('cards.water.unit'),
                icon: <WaterDropRoundedIcon />,
                color: theme.palette.water.main,
                dataPct: meanByType(rows$, 'water', 'one_year_data_pct'),
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'water'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'water')
                    .map((item) => item.main_address),
                ).size,
                summedData: waterSummedData ?? [],
              },
            ].map((service) => (
              <Grid
                key={service.label}
                size={{
                  md: 12,
                  lg: 4,
                }}
              >
                <PortfolioAnalysisServiceCard {...service} />
              </Grid>
            ))}
        </Suspense>
        <Grid maxHeight={'80vh'} size={12}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '80vh',
                  verticalAlign: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Box
              maxHeight={isFullscreen ? '80vh' : '60vh'}
              display={'flex'}
              flexDirection={'column'}
            >
              <DatagridComponent
                data={enrichedData}
                matrikelList={matrikel_list}
                setSelectedMeter={handleSetSelectedMeter}
                setMeterType={setMeterType}
                outOfSync={false}
                isLoading={isFetching}
              />
            </Box>
          </Suspense>
        </Grid>
      </Grid>
      <DataIntegrityDialog />
    </PageContainer>
  );
};

export default PortfolioAnalysisDashboard;
